// src/pages/auth/ForgotPasswordOTP.js
// for password reset OTP verification
import React, { useEffect, useState, useRef } from "react";

import { useMainPageTitle } from "../../contexts/PageContext";
import { useLocation, useNavigate } from "react-router-dom";
const API_URL = "https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1";

export default function ForgotPasswordOTP() {
  const { updatePageTitle } = useMainPageTitle();

  const navigate = useNavigate();
  const inputRefs = useRef([]);

  useEffect(() => {
    updatePageTitle(
      "Forgot Password OTP",
      "Enter your OTP to reset password"
    );
  }, [updatePageTitle]);

  const { state } = useLocation();
  const { email } = state || {};
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [errors, setErrors] = useState({});

  const handleOtpChange = (e, index) => {
    const value = e.target.value;
    
    // Handle paste event (detected by longer input)
    if (value.length > 1) {
      // This could be a paste operation
      const pastedValue = value.slice(0, 6); // Limit to 6 digits
      const newOtp = [...otp];
      
      // Fill the OTP array with the pasted characters
      for (let i = 0; i < pastedValue.length; i++) {
        if (index + i < 6) {
          newOtp[index + i] = pastedValue[i];
        }
      }
      
      setOtp(newOtp);
      
      // Focus on the appropriate input after paste
      const focusIndex = Math.min(index + pastedValue.length, 5);
      if (inputRefs.current[focusIndex]) {
        inputRefs.current[focusIndex].focus();
      }
      return;
    }
    
    // Normal single digit input
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    
    // Focus on the next input
    if (value.length === 1 && index < 5) {
      if (inputRefs.current[index + 1]) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  const handleKeyDown = (e, index) => {
    // Handle backspace
    if (e.key === "Backspace") {
      if (otp[index] === "") {
        // Current field is empty, move to previous field
        if (index > 0) {
          const newOtp = [...otp];
          newOtp[index - 1] = "";
          setOtp(newOtp);
          if (inputRefs.current[index - 1]) {
            inputRefs.current[index - 1].focus();
          }
        }
      }
    }
    
    // Handle left arrow key
    if (e.key === "ArrowLeft" && index > 0) {
      if (inputRefs.current[index - 1]) {
        inputRefs.current[index - 1].focus();
      }
    }
    
    // Handle right arrow key
    if (e.key === "ArrowRight" && index < 5) {
      if (inputRefs.current[index + 1]) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  // Handle paste event at container level
  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text/plain").trim();
    
    // Only process if it looks like a numeric code
    if (pasteData.match(/^[0-9]+$/) && pasteData.length <= 6) {
      const newOtp = [...otp];
      
      // Fill the OTP array with the pasted characters
      for (let i = 0; i < Math.min(pasteData.length, 6); i++) {
        newOtp[i] = pasteData[i];
      }
      
      setOtp(newOtp);
      
      // Focus on the last filled input or the last input
      const focusIndex = Math.min(pasteData.length - 1, 5);
      if (inputRefs.current[focusIndex]) {
        inputRefs.current[focusIndex].focus();
      }
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    setErrors({});
    if (otp.some((value) => value === "")) {
      errors.otp = "OTP must be 6 digits";
      setErrors(errors);
      return;
    }

    if (Object.keys(errors).length > 0) {
      return;
    }
    const otpString = otp.join("");
    
    const data = {
      email: email.trim().toLowerCase(),
      otp: otpString,
    };

    try {
      const response = await fetch(`${API_URL}/auth/forgot-password/verify`, {
        method: "POST",
        body: JSON.stringify(data),
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
      });

      const responseText = await response.text();
      console.log('Response status:', response.status);
      console.log('Response text:', responseText);

      let responseData;
      try {
        responseData = JSON.parse(responseText);
        navigate("/reset-password", {
          state: {
            email: responseData.data.email,
            token: responseData.data.resetPasswordToken,
          },
        });
      } catch (e) {
        console.error('Failed to parse JSON:', e);
        setErrors({ general: "Invalid server response" });
        return;
      }

      if (!response.ok) {
        if (response.status === 404) {
          setErrors({ general: "User not found. Please sign up first." });
        } else if (response.status === 400) {
          setErrors({ 
            otp: responseData.errors || responseData.message || "Invalid OTP code"
          });
        } else if (response.status === 500) {
          setErrors({ general: "Server error. Please try again later." });
        } else {
          setErrors({ 
            general: responseData.errors || responseData.message || "Verification failed" 
          });
        }
        return;
      }

    } catch (error) {
      console.error("Error during OTP verification:", error);
      setErrors({
        general: "Failed to connect to the server. Please try again later.",
      });
    }
  };

  return (
    <div className="max-w-md mx-auto">
      {/* Main OTP Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 lg:p-8">
        
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Verify Reset Code</h2>
          <p className="text-gray-600">
            We've sent a 6-digit code to <span className="font-medium text-gray-800">{email}</span>
          </p>
        </div>

        {/* Error Messages */}
        {errors.general && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <p className="text-sm font-medium text-red-800">{errors.general}</p>
            </div>
          </div>
        )}

        {errors.otp && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-sm font-medium text-red-800">{errors.otp}</p>
          </div>
        )}

        {/* OTP Form */}
        <form onSubmit={handleOtpSubmit} className="space-y-6">
          
          {/* OTP Input Fields */}
          <div className="flex justify-center">
            <div 
              className="flex gap-3" 
              onPaste={handlePaste}
            >
              {otp.map((value, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength="1"
                  value={otp[index]}
                  onChange={(e) => handleOtpChange(e, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  name={`otp-${index}`}
                  className="w-12 h-12 text-center text-lg font-semibold border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F15213] focus:border-[#F15213] transition-colors"
                  autoComplete="off"
                />
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="w-full bg-[#F15213] hover:bg-[#F15213]/90 text-white font-semibold py-3 px-4 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#F15213] focus:ring-offset-2"
          >
            Verify Code
          </button>
        </form>

        {/* Help Text */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            Didn't receive the code?{" "}
            <button 
              type="button"
              className="text-[#F15213] hover:text-[#F15213]/80 font-medium"
              onClick={() => {
                // Add resend OTP logic here
                console.log("Resend OTP");
              }}
            >
              Resend Code
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
